import React, { FC, FocusEvent, MouseEvent, useEffect, useState } from "react";

import { LinkHandler } from "@/components/common/LinkHandler";
import { MinusSmallIcon } from "@/components/icons/MinusSmallIcon";
import { PlusSmallIcon } from "@/components/icons/PlusSmallIcon";
import { TextElementNova } from "@/components/ui-kit/TextElementNova/TextElementNova";
import { HeaderDataItem } from "@/data/navigation/header.data";
import { useNavigation } from "@/hooks/useNavigation";
import { PromoData } from "@/types/promoData";
import { GA4 } from "@/utils/ga4Utils";
import { cn } from "@/utils/general";
import { segmentEvent } from "@/utils/segmentfunctions";

import MenNavigationScience from "./MenNavigationScience";
import MenNavigationShop from "./MenNavigationShop";
import styles from "./Navigation.module.scss";
import NavigationScience from "./NavigationScience";
import NavigationShop from "./NavigationShop";

interface NavigationProps {
  data: HeaderDataItem[];
  promoCf: PromoData;
}

const Navigation: FC<NavigationProps> = ({ data, promoCf }) => {
  const [hideSubMenu, setHideSubMenu] = useState<boolean>(false);
  const navigate = useNavigation();

  const trackExpanded = (text: string) => {
    segmentEvent("Navigation Expanded", {
      text,
    });
  };

  useEffect(() => {
    if (hideSubMenu) {
      setHideSubMenu(false);
    }
  }, [hideSubMenu]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (!(event.target as HTMLElement).closest(".master-nav-ul")) {
        removeAllActiveItemClass();
      }
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    document.addEventListener("mousedown", handleOutsideClick as any); // Type assertion for event listener
    return () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      document.removeEventListener("mousedown", handleOutsideClick as any);
    };
  }, []);

  const addActiveItemClass = (el: HTMLElement) => {
    if (el && el.classList) {
      el.classList.add(styles["hover"]);
      if (el.classList.contains(styles["has-subnav"])) {
        handleExpand(el);
      }
    }
  };

  const removeActiveItemClass = (el: HTMLElement) => {
    //return false;
    const hasHover = el.closest(`.${styles["hover"]}`);
    const hasSubnav = el.closest(`.${styles["has-subnav"]}.${styles["hover"]}`);
    if (!!hasHover) {
      hasHover.classList.remove(styles["hover"]);
    }
    if (!!hasSubnav) {
      handleExpand(el);
    }
  };

  const handleExpand = (el: HTMLElement | null) => {
    if (el?.id) {
      document
        .querySelectorAll<HTMLElement>(`.${styles["nav-item-link"]}[aria-expanded]`)
        .forEach((a) => {
          const isHover = a?.parentElement?.classList?.contains(styles["hover"]);
          a.ariaExpanded = isHover ? "true" : "false";
        });
    }
  };

  const onFocusHandler = (el: HTMLElement) => {
    addActiveItemClass(el);
  };

  const onBlurHandler = (el: HTMLElement) => {
    setTimeout(() => {
      if (!el.contains(document.activeElement)) {
        removeActiveItemClass(el);
      }
    }, 50);
  };

  const removeAllActiveItemClass = () => {
    document.querySelectorAll<HTMLElement>(`.${styles["hover"]}`).forEach((el) => {
      el.classList.remove(styles["hover"]);
      const ariaExpandedEl = el.querySelector<HTMLElement>(
        `.${styles["nav-item-link"]}[aria-expanded]`
      );
      if (ariaExpandedEl) {
        ariaExpandedEl.ariaExpanded = "false";
      }
    });
  };

  const trackClicked = (text: string, destination: string) => {
    GA4.onNavigationClicked({
      type: "button",
      text,
      destination,
    });
  };

  const navLinks = data.map((item, index) => {
    const isHasSubnav = !!item.children;
    const itemClasses = cn(
      "nav-list-item box-content relative inline-block align-top text-nova-base-black transition-padding duration-300 ease-in-out",
      isHasSubnav && styles["has-subnav"],
      styles["nav-list-item"],
      item.name === "shop" && "min-w-[64px]",
      item.name === "science" && "min-w-[60px]",
      item.name === "sciencemen" && "min-w-[60px]",
      item.name === "results" && "min-w-[60px]"
    );

    const linkTextClasses = cn(
      "Body-20-Dia-Normal leading-[22px] text-nova-base-black link-text",
      styles["link-text"],
      styles[item.name]
    );

    const showItem = !item.isMobileOnly;

    return (
      <React.Fragment key={`navLink-${index}`}>
        {showItem && (
          <li
            id={item.name}
            key={`navitem-${index}`}
            className={cn(itemClasses, item.className)}
            onMouseEnter={(e: MouseEvent<HTMLLIElement>) => {
              if (isHasSubnav) {
                trackExpanded(item.link.text);
              }
              addActiveItemClass(e.currentTarget);
            }}
            onFocus={(e: FocusEvent<HTMLLIElement>) => onFocusHandler(e.currentTarget)}
            onBlur={(e: FocusEvent<HTMLLIElement>) => onBlurHandler(e.currentTarget)}
            onMouseLeave={(e: MouseEvent<HTMLLIElement>) => removeActiveItemClass(e.currentTarget)}
            onClick={(e: MouseEvent<HTMLLIElement>) => {
              if (isHasSubnav) {
                const isCurrentlyHovered = e.currentTarget.classList.contains(styles["hover"]);
                removeAllActiveItemClass();
                if (!isCurrentlyHovered) {
                  addActiveItemClass(e.currentTarget);
                }
              } else if (item.link.url !== "#") {
                trackClicked(item.link.text, item.link.url);
                navigate(item.link.url);
              }
            }}
            data-linkname={item.name}
            role="menuitem"
            aria-label={item.link.text}
          >
            <LinkHandler
              path={item.link.url}
              className={cn(
                "relative inline-block w-full h-full nav-item-link",
                isHasSubnav ? "has-children" : "no-children",
                styles["nav-item-link"]
              )}
              onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                /*
                 * `debugger` statement for debugging, remove in production
                 * debugger;
                 */
                if (isHasSubnav) {
                  e.preventDefault();
                } else {
                  trackClicked(item.link.text, item.link.url);
                  navigate(item.link.url);
                }
              }}
            >
              <div className="flex items-center gap-2">
                <div>
                  <TextElementNova text={item.link.text} className={linkTextClasses} />
                </div>
                {isHasSubnav && (
                  <div className={cn("inline-block w-5", styles["arrow-section"])}>
                    <PlusSmallIcon
                      className={cn("block text-nova-base-black", styles["plus-icon"])}
                    />
                    <MinusSmallIcon className={cn("hidden", styles["minus-icon"])} />
                  </div>
                )}
              </div>
            </LinkHandler>

            {isHasSubnav && !hideSubMenu && (
              <div
                className={cn(
                  `dropdown-${item.name}-desktop dropdown absolute z-10 w-full left-0`,
                  styles["dropdown"]
                )}
              >
                {item.name === "science" ? (
                  <NavigationScience
                    // @ts-expect-error - this is a workaround for a bug in the browser
                    item={data.find((d) => d.name === "science") as HeaderDataItem}
                  />
                ) : null}

                {item.name === "sciencemen" ? (
                  <MenNavigationScience
                    // @ts-expect-error - this is a workaround for a bug in the browser
                    item={data.find((d) => d.name === "sciencemen") as HeaderDataItem}
                  />
                ) : null}

                {item.name === "shop" ? (
                  <NavigationShop
                    promoCf={promoCf}
                    item={data.find((d) => d.name === "shop") as HeaderDataItem}
                  />
                ) : null}

                {item.name === "shopmen" ? (
                  <div className={cn("flex-row ", styles.dropdownWrapper)}>
                    <MenNavigationShop promoCf={promoCf} item={item} navigate={navigate} />
                  </div>
                ) : null}
              </div>
            )}
          </li>
        )}
      </React.Fragment>
    );
  });

  return (
    <nav
      className={cn(
        "hidden lg:inline-block lg:static lg:w-full lg:h-auto lg:p-0",
        styles["main-nav-container"],
        "navigation-variant"
      )}
    >
      <ul
        className={cn("master-nav-ul", styles["master-nav-ul"])}
        role="menubar"
        aria-label="Navigation"
      >
        {navLinks}
      </ul>
    </nav>
  );
};

export default React.memo(Navigation);
